import { ParsedUrlQuery } from 'querystring';
import { InnerFeedContext } from '@interfaces/models/elasticSearch';
import { SupportedLanguages } from '@interfaces/models/language';

interface Params {
  [key: string]: string | string[] | undefined;
}

export const isPersonalizedNewInsPage = (url: string): boolean => url.includes('/new-items-for-you/');

export const isSearchPage = (url: string): boolean => {
  return /\bsearch\b/.test(url);
};

export const isDealHuntingLandingPage = (query: ParsedUrlQuery): boolean => {
  return query?.innerFeedContext === InnerFeedContext.DealHunting;
};

export const isDirectShippingPage = (url: string): boolean => {
  const directShippingPageUrls = [
    '/direct-shipping/',
    '/envoi-direct/',
    '/spedizione-diretta/',
    '/envio-directo/',
    '/direktversand/',
    '/direktleverans/',
    '/directe-verzending/',
  ];
  return directShippingPageUrls.some((path) => url?.includes(path));
};

export const isExpressDeliveryPage = (url: string): boolean => {
  const expressDeliveryUrls = [
    '/express-delivery/',
    '/livraison-express/',
    '/consegna-express/',
    '/entrega-express/',
    '/expressversand/',
    '/express-levering/',
    '/expressfrakt/',
  ];
  return expressDeliveryUrls.some((path) => url?.includes(path));
};

export const isCatalogPage = (url: string): boolean => {
  return isSearchPage(url) || isDirectShippingPage(url) || isExpressDeliveryPage(url);
};

export const isCatalogOrCampaignPage = (url: string): boolean => {
  return isCatalogPage(url) || url.startsWith('/c/');
};

export const isHomePage = (pathname: string, asPath?: string): boolean => {
  const path = asPath || pathname;
  return pathname === '/' || path === '/' || path.startsWith('/womenswear') || path.startsWith('/menswear');
};

export const isEditorialPage = (url: string): boolean => {
  const pathSegments = url.split('/') || [];

  return pathSegments[0] === 'journal';
};

export const isPricingCalculatorPage = (url: string): boolean => {
  return /\bsell\/pricing-calculator\b/.test(url);
};

export const isLandingPage = (url: string): boolean => {
  const firstPathName = 'cms';
  const secondLevelPathNames = [
    'web-ds-ed',
    'talking-fashion',
    'app-ds-ed',
    'channels',
    'top-seller',
    'homemade-by-vc',
    'my-community',
    'raas',
    'daily-deals',
    'description',
  ];
  const pathSegments = (url.split('/') || []).filter((item) => item);

  if (pathSegments.length < 2) {
    return false;
  }

  return !!(pathSegments[0] === firstPathName && secondLevelPathNames.includes(pathSegments[1]));
};

export const isWebview = (query: ParsedUrlQuery): boolean => {
  if (!query) {
    return false;
  }

  return !!(query.webView || query.webview || query.native);
};

export const getLandingPageType = (url: string): string => {
  if (!url) {
    return null;
  }

  const pathAsArr = url.split('/')?.filter((path) => !!path);

  if (pathAsArr[0] === 'cms') {
    return pathAsArr[1];
  }

  return null;
};

export const getSearchKeyword = (query: string): string => {
  if (query) {
    return query;
  }
  return 'no keyword';
};

const NEW_IN_PATHS_BY_LANGUAGE: Record<SupportedLanguages, string> = {
  en: 'new-items',
  ko: 'new-items',
  us: 'new-items',
  hk: 'new-items',
  fr: 'nouveautes',
  it: 'novita',
  es: 'novedades',
  de: 'neuheiten',
  nl: 'nieuwe-items',
  sv: 'nyheter',
};

export const isNewInPage = (url: string): boolean => {
  return Object.values(NEW_IN_PATHS_BY_LANGUAGE).some((path) => url.includes(path));
};

export const getNewInPathByLanguage = (language?: SupportedLanguages): string => {
  return NEW_IN_PATHS_BY_LANGUAGE[language] || NEW_IN_PATHS_BY_LANGUAGE.en; // Default to English
};

const encodeQueryValue = (value: string | string[] | undefined): string => {
  if (typeof value === 'string') {
    return encodeURIComponent(value);
  }
  if (Array.isArray(value) && value.length > 0) {
    return encodeURIComponent(value[0]);
  }
  return '';
};

export const extractUrlfromPrerenderDataReq = (contextQuery: ParsedUrlQuery, slug: string): string => {
  const slugValue = contextQuery[slug];
  const url = Array.isArray(slugValue) ? slugValue.join('/') : slugValue;
  const queryStr = Object.keys(contextQuery)
    .filter((key) => key !== slug)
    .map((key) => `${key}=${encodeQueryValue(contextQuery[key])}`)
    .join('&');

  const queryPrefix = queryStr !== '' ? '?' : '';

  return `/${url}/${queryPrefix}${queryStr}`;
};

// TODO: Can be replaced with `updateParamsInUrl`?
export const updateParamInUrl = (name: string, value: string) => {
  const { search, pathname, origin, hash } = window.location;
  const queryParams = new URLSearchParams(search);
  if (queryParams.get(name) === null) {
    queryParams.append(name, value);
  } else {
    queryParams.set(name, value);
  }
  const queryString = queryParams.toString();
  const newUrl = `${origin}${pathname}${queryString ? `?${queryParams}` : ''}${hash}`;
  window.history.replaceState(null, undefined, newUrl);
};

/**
 * Updates the URL's query parameters to update in the URL based on the provided `params` object.
 * - If a parameter value is `undefined` or an empty string, the parameter is removed from the URL.
 */

export const updateParamsInUrl = (params: Params): void => {
  if (typeof window !== 'undefined') {
    const { search, pathname, origin, hash } = window.location;
    const queryParams = new URLSearchParams(search);

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        queryParams.delete(key);
      } else {
        if (Array.isArray(value)) {
          queryParams.set(key, value.join(','));
        } else {
          queryParams.set(key, value);
        }
      }
    });

    const queryString = queryParams.toString();
    const newUrl = `${origin}${pathname}${queryString ? `?${queryString}` : ''}${hash}`;

    // https://github.com/vercel/next.js/discussions/18072
    window.history.pushState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
  }
};

export const isQcReportV2 = (url: string) => url.includes('quality-control-report/v2');

export const getFaqUrl = (locale: string) => {
  return `https://faq.vestiairecollective.com/hc/${locale?.toLowerCase() || 'en-us'}`;
};
